import React, { useState } from "react";

import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import "./AdminNotifications.css";

const AdminNotifications = () => {
  // =========================
  // SEARCH USER
  // =========================
  const [username, setUsername] = useState("");
  const [searchedUser, setSearchedUser] = useState(null);
  const [searching, setSearching] = useState(false);

  // =========================
  // FORM
  // =========================
  const [category, setCategory] = useState("messages");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [actionText, setActionText] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [symbol, setSymbol] = useState("");

  // =========================
  // STATUS
  // =========================
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // =========================
  // SEARCH USERNAME
  // =========================
  const handleSearchUser = async () => {
    setSuccess("");
    setError("");
    setSearchedUser(null);

    const cleanUsername = username.trim();

    if (!cleanUsername) {
      setError("Andika username kwanza.");
      return;
    }

    try {
      setSearching(true);

      const usersQuery = query(
        collection(db, "users"),
        where("username", "==", cleanUsername)
      );

      const snap = await getDocs(usersQuery);

      if (snap.empty) {
        setError(`Username "${cleanUsername}" haijapatikana.`);
        return;
      }

      const userDoc = snap.docs[0];

      setSearchedUser({
        id: userDoc.id,
        ...userDoc.data(),
      });
    } catch (err) {
      console.error("Search user error:", err);
      setError("Imeshindikana kumtafuta user.");
    } finally {
      setSearching(false);
    }
  };

  // =========================
  // CLEAR FORM
  // =========================
  const clearForm = () => {
    setTitle("");
    setMessage("");
    setActionText("");
    setActionUrl("");
    setSymbol("");
    setSuccess("");
    setError("");
  };

  // =========================
  // ADD NOTIFICATION
  // =========================
  const handleAddNotification = async () => {
    setSuccess("");
    setError("");

    if (!searchedUser) {
      setError("Search username kwanza.");
      return;
    }

    if (!title.trim()) {
      setError("Weka notification title.");
      return;
    }

    if (!message.trim()) {
      setError("Weka notification message.");
      return;
    }

    try {
      setSaving(true);

      const notificationData = {
        username: searchedUser.username || username.trim(),

        category: category,

        title: title.trim(),

        message: message.trim(),

        actionText: actionText.trim(),

        actionUrl: actionUrl.trim(),

        symbol: symbol.trim(),

        read: false,

        createdAt: serverTimestamp(),
      };

      await addDoc(
        collection(db, "notifications"),
        notificationData
      );

      setSuccess(
        `Notification imetumwa kwa ${searchedUser.username}.`
      );

      // Safisha form lakini user aliyepatikana abaki selected
      setTitle("");
      setMessage("");
      setActionText("");
      setActionUrl("");
      setSymbol("");
    } catch (err) {
      console.error("Add notification error:", err);
      setError("Imeshindikana kuongeza notification.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // QUICK TEMPLATES
  // =========================
  const applyTransactionPreset = () => {
    setCategory("transactions");

    setTitle("Transaction Update");

    setMessage(
      "Your transaction has been received and is currently being processed."
    );

    setActionText("View Transaction");

    setActionUrl("/history");

    setSymbol("");
  };

  const applyMarketPreset = () => {
    setCategory("markets");

    setTitle("Market Alert!");

    setMessage(
      "📈 Strong market movement detected. Keep an eye on the market."
    );

    setActionText("View Symbol");

    setActionUrl("/markets");
  };

  const applyAccountPreset = () => {
    setCategory("account");

    setTitle("Your account is approved");

    setMessage(
      "Your account has been approved successfully. You can now continue using all available services."
    );

    setActionText("Proceed");

    setActionUrl("/deposit");
  };

  const applyMessagePreset = () => {
    setCategory("messages");

    setTitle("Important Message");

    setMessage(
      "This is an important message from the administration."
    );

    setActionText("");

    setActionUrl("");
  };

  const applyNewsPreset = () => {
    setCategory("news");

    setTitle("BFC News");

    setMessage(
      "Stay updated with the latest news and important announcements."
    );

    setActionText("View");

    setActionUrl("/news");
  };

  return (
    <div className="admin-notification-page">
      {/* =========================
          HEADER
      ========================= */}
      <div className="admin-notification-header">
        <div>
          <span className="admin-notification-kicker">
            ADMIN PANEL
          </span>

          <h1>Notifications</h1>

          <p>
            Send a notification directly to a user.
          </p>
        </div>
      </div>

      {/* =========================
          SEARCH USER
      ========================= */}
      <section className="admin-notification-card">
        <div className="admin-section-title">
          <span className="admin-section-number">01</span>

          <div>
            <h2>Find User</h2>

            <p>
              Search username before sending notification.
            </p>
          </div>
        </div>

        <div className="admin-search-row">
          <input
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSearchUser();
              }
            }}
            placeholder="Enter username..."
            autoComplete="off"
          />

          <button
            type="button"
            className="admin-search-btn"
            onClick={handleSearchUser}
            disabled={searching}
          >
            {searching ? "Searching..." : "Search User"}
          </button>
        </div>

        {searchedUser && (
          <div className="admin-user-found">
            <div className="admin-user-avatar">
              {String(searchedUser.username || "U")
                .charAt(0)
                .toUpperCase()}
            </div>

            <div className="admin-user-info">
              <span>USER FOUND</span>

              <strong>{searchedUser.username}</strong>
            </div>

            <div className="admin-user-status">
              ● READY
            </div>
          </div>
        )}
      </section>

      {/* =========================
          QUICK TEMPLATES
      ========================= */}
      {searchedUser && (
        <section className="admin-notification-card">
          <div className="admin-section-title">
            <span className="admin-section-number">02</span>

            <div>
              <h2>Quick Templates</h2>

              <p>
                Select a notification type.
              </p>
            </div>
          </div>

          <div className="admin-presets">
            <button
              type="button"
              onClick={applyTransactionPreset}
            >
              Transaction
            </button>

            <button
              type="button"
              onClick={applyMarketPreset}
            >
              Markets
            </button>

            <button
              type="button"
              onClick={applyMessagePreset}
            >
              Messages
            </button>

            <button
              type="button"
              onClick={applyAccountPreset}
            >
              Account
            </button>

            <button
              type="button"
              onClick={applyNewsPreset}
            >
              BFC News
            </button>
          </div>
        </section>
      )}

      {/* =========================
          NOTIFICATION FORM
      ========================= */}
      {searchedUser && (
        <section className="admin-notification-card">
          <div className="admin-section-title">
            <span className="admin-section-number">03</span>

            <div>
              <h2>Add Notification</h2>

              <p>
                Create the message that the user will receive.
              </p>
            </div>
          </div>

          {/* CATEGORY */}
          <div className="admin-field">
            <label>Notification Type</label>

            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="transactions">
                Transaction
              </option>

              <option value="markets">
                Markets
              </option>

              <option value="messages">
                Messages
              </option>

              <option value="account">
                Account
              </option>

              <option value="news">
                BFC News
              </option>
            </select>
          </div>

          {/* TITLE */}
          <div className="admin-field">
            <label>Title</label>

            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Example: Transaction Update"
            />
          </div>

          {/* MESSAGE */}
          <div className="admin-field">
            <label>Message</label>

            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Write notification message..."
              rows={7}
            />
          </div>

          {/* ACTION TEXT */}
          <div className="admin-field">
            <label>
              Button Text
              <span>Optional</span>
            </label>

            <input
              type="text"
              value={actionText}
              onChange={(event) =>
                setActionText(event.target.value)
              }
              placeholder="Example: View Transaction"
            />
          </div>

          {/* ACTION URL */}
          <div className="admin-field">
            <label>
              Button URL
              <span>Optional</span>
            </label>

            <input
              type="text"
              value={actionUrl}
              onChange={(event) =>
                setActionUrl(event.target.value)
              }
              placeholder="Example: /history"
            />
          </div>

          {/* SYMBOL */}
          {category === "markets" && (
            <div className="admin-field">
              <label>
                Symbol
                <span>Optional</span>
              </label>

              <input
                type="text"
                value={symbol}
                onChange={(event) =>
                  setSymbol(event.target.value)
                }
                placeholder="Example: USOIL"
              />
            </div>
          )}

          {/* PREVIEW */}
          <div className="admin-preview-label">
            PREVIEW
          </div>

          <div
            className={`admin-notification-preview ${category}`}
          >
            <div className="admin-preview-title">
              {title || "Notification title"}
            </div>

            <div className="admin-preview-line" />

            <div className="admin-preview-body">
              <p>
                {message ||
                  "Notification message will appear here."}
              </p>

              <div className="admin-preview-footer">
                <span>
                  User: {searchedUser.username}
                </span>

                {actionText && (
                  <button type="button" disabled>
                    {actionText}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ACTIONS */}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-clear-btn"
              onClick={clearForm}
              disabled={saving}
            >
              Clear
            </button>

            <button
              type="button"
              className="admin-send-btn"
              onClick={handleAddNotification}
              disabled={saving}
            >
              {saving ? "Sending..." : "Add Notification"}
            </button>
          </div>
        </section>
      )}

      {/* =========================
          SUCCESS
      ========================= */}
      {success && (
        <div className="admin-notification-success">
          <span>✓</span>
          {success}
        </div>
      )}

      {/* =========================
          ERROR
      ========================= */}
      {error && (
        <div className="admin-notification-error">
          <span>!</span>
          {error}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;